import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DirectDealFilterOptionValueSorterEnum } from '../../constants/filter/directDealFilter.constant';
import { priceSliderValueMapping } from '../../constants/filter/slider/filterSliderConstant';
import { filterSlice } from '../../store/filterSlice';
import { useAppDispatch, useAppSelector } from '../store/useStore';

export const useUpdateDirectDealFilterStates = () => {
  const dispatch = useAppDispatch();
  const directDealFilterStates = useAppSelector((state) => state.filter.directDealFilter);
  const filterActions = filterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, [searchParams]);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, [searchParams]);

  const minEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('minEngineOutputPs')) {
      return searchParams.get('minEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const maxEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxEngineOutputPs')) {
      return searchParams.get('maxEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const minPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('minPrice')) {
      return searchParams.get('minPrice')!;
    }
    return '';
  }, [searchParams]);

  const maxPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxPrice')) {
      return searchParams.get('maxPrice')!;
    }
    return '';
  }, [searchParams]);

  const statusSearchParam = useMemo<string>(() => {
    if (searchParams.has('status')) {
      return searchParams.get('status')!;
    }
    return '';
  }, [searchParams]);

  const machineTypeSearchParam = useMemo<string>(() => {
    if (searchParams.has('machineType')) {
      return searchParams.get('machineType')!;
    }
    return '';
  }, [searchParams]);

  const rowConfigSearchParam = useMemo<string>(() => {
    if (searchParams.has('rowConfig')) {
      return searchParams.get('rowConfig')!;
    }
    return '';
  }, [searchParams]);

  const minOperationTimeSearchParam = useMemo<string>(() => {
    if (searchParams.has('minOperationTime')) {
      return searchParams.get('minOperationTime')!;
    }
    return '';
  }, [searchParams]);

  const maxOperationTimeSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxOperationTime')) {
      return searchParams.get('maxOperationTime')!;
    }
    return '';
  }, [searchParams]);

  // 실제 가격을 슬라이더 값으로 변환하는 함수
  const convertPriceToSliderValue = (price: number): number | undefined => {
    const entry = Object.entries(priceSliderValueMapping).find(([_, mappedPrice]) => mappedPrice === price);
    return entry ? Number(entry[0]) : undefined;
  };

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!directDealFilterStates.isSearchParamChecked && directDealFilterStates.isFilterOptionsAdded.brands) {
      dispatch(filterActions.resetAllFilters({ filterType: 'directDealFilter' }));

      // searchParams.size === 0가 있을 때만 필터 업데이트
      if (searchParams && searchParams.size > 0) {
        // 정렬 필터 업데이트
        if (sortSearchParam) {
          Object.values(DirectDealFilterOptionValueSorterEnum).forEach((sortOptionValues) => {
            if (sortSearchParam.includes(sortOptionValues as string)) {
              const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValues);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'directDealFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // 브랜드 필터 업데이트
        if (brandSearchParam) {
          brandSearchParam.split(',').forEach((brandOptionValue) => {
            const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'filter_brand');
            const targetOption = targetFilter?.options.find((option) => option.value === brandOptionValue);

            if (targetFilter && targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 마력 필터 업데이트
        if (minEngineOutputPsSearchParam && maxEngineOutputPsSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'engineOutputInPs');

          if (targetFilter) {
            dispatch(
              filterActions.changeFilterOptionValue({
                filterType: 'directDealFilter',
                targetFilter,
                optionValues: [Number(minEngineOutputPsSearchParam), Number(maxEngineOutputPsSearchParam)],
              }),
            );
          }
        }

        // 가격 필터 업데이트
        if (minPriceSearchParam && maxPriceSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'price');

          if (targetFilter) {
            const minPrice = Number(minPriceSearchParam);
            const maxPrice = Number(maxPriceSearchParam);

            const minSliderValue = convertPriceToSliderValue(minPrice);
            const maxSliderValue = convertPriceToSliderValue(maxPrice);

            if (minSliderValue !== undefined && maxSliderValue !== undefined) {
              dispatch(
                filterActions.changeFilterOptionValue({
                  filterType: 'directDealFilter',
                  targetFilter,
                  optionValues: [minSliderValue, maxSliderValue],
                }),
              );
            }
          }
        }

        // 판매 상태 필터 업데이트
        if (statusSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'status');

          if (targetFilter) {
            const targetOption = targetFilter?.options.find((option) => option.value === statusSearchParam);

            if (targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          }
        }

        // 기계 타입 필터 업데이트
        if (machineTypeSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'machineType');

          if (targetFilter) {
            const targetOption = targetFilter?.options.find((option) => option.value === machineTypeSearchParam);

            if (targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          }
        }

        // 조식 필터 업데이트
        if (rowConfigSearchParam) {
          rowConfigSearchParam.split(',').forEach((rowConfigOptionValue) => {
            const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'rowConfig');

            if (targetFilter) {
              const targetOption = targetFilter?.options.find((option) => option.value === rowConfigOptionValue);

              if (targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'directDealFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // 사용시간 필터 업데이트
        if (minOperationTimeSearchParam && maxOperationTimeSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'operationTime');

          if (targetFilter) {
            dispatch(
              filterActions.changeFilterOptionValue({
                filterType: 'directDealFilter',
                targetFilter,
                optionValues: [Number(minOperationTimeSearchParam), Number(maxOperationTimeSearchParam)],
              }),
            );
          }
        }
      }

      // 마지막으로 isSearchParamChecked를 true로 설정
      dispatch(filterActions.changeIsSearchParamChecked({ filterType: 'directDealFilter', value: true }));
    }
  }, [
    dispatch,
    filterActions,
    directDealFilterStates.globalFilters,
    directDealFilterStates.isSearchParamChecked,
    directDealFilterStates.isFilterOptionsAdded.brands,
    searchParams,
    searchParams.size,
    brandSearchParam,
    sortSearchParam,
    minEngineOutputPsSearchParam,
    maxEngineOutputPsSearchParam,
    minPriceSearchParam,
    maxPriceSearchParam,
    statusSearchParam,
    machineTypeSearchParam,
    rowConfigSearchParam,
    minOperationTimeSearchParam,
    maxOperationTimeSearchParam,
  ]);
};
